/**
 * PaymentMethodController - Settings card for payment method configuration
 *
 * Composed component: No internal dependencies.
 *
 * Features:
 * - Toggle active/inactive status (green/red)
 * - Editable min/max amount inputs
 * - Card-style layout
 */

import { cn } from '@/lib/utils';

export interface PaymentMethodControllerProps {
  /** Payment method name */
  name: string;
  /** Whether the method is active */
  isActive: boolean;
  /** Toggle active status */
  onToggle: (active: boolean) => void;
  /** Minimum amount */
  minAmount: number | string;
  /** Maximum amount */
  maxAmount: number | string;
  /** Min amount change handler */
  onMinChange: (value: string) => void;
  /** Max amount change handler */
  onMaxChange: (value: string) => void;
  /** Currency suffix */
  suffix?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Loading state for toggle */
  isToggling?: boolean;
}

export function PaymentMethodController({
  name,
  isActive,
  onToggle,
  minAmount,
  maxAmount,
  onMinChange,
  onMaxChange,
  suffix = '₼',
  disabled = false,
  isToggling = false,
}: PaymentMethodControllerProps) {
  const handleToggle = () => {
    if (disabled || isToggling) return;
    onToggle(!isActive);
  };

  return (
    <div
      className={cn(
        'w-full rounded-2xl bg-white p-4',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'border border-slate-200',
        disabled && 'opacity-50',
      )}
    >
      {/* Header: Name + Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-800">{name}</h3>

        {/* Toggle Button */}
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled || isToggling}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full',
            'text-sm font-medium',
            'transition-all duration-200',
            isActive
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-rose-100 text-rose-700',
            !disabled && !isToggling && 'hover:opacity-80 active:opacity-70',
            (disabled || isToggling) && 'cursor-not-allowed',
          )}
        >
          {isToggling ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  isActive ? 'bg-emerald-500' : 'bg-rose-500',
                )}
              />
              {isActive ? 'Active' : 'Inactive'}
            </>
          )}
        </button>
      </div>

      {/* Min/Max Inputs */}
      <div className="flex items-center gap-3">
        {/* Min Input */}
        <div className="flex-1">
          <label className="block text-xs text-slate-500 mb-1">Min</label>
          <div className="relative">
            <input
              type="number"
              value={minAmount}
              onChange={(e) => onMinChange(e.target.value)}
              disabled={disabled}
              placeholder="0"
              className={cn(
                'w-full px-3 py-2 pr-8 rounded-xl',
                'bg-slate-50 border border-slate-200',
                'text-sm text-slate-700 placeholder:text-slate-400',
                'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent',
                'transition-all',
                disabled && 'cursor-not-allowed',
              )}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {suffix}
            </span>
          </div>
        </div>

        {/* Max Input */}
        <div className="flex-1">
          <label className="block text-xs text-slate-500 mb-1">Max</label>
          <div className="relative">
            <input
              type="number"
              value={maxAmount}
              onChange={(e) => onMaxChange(e.target.value)}
              disabled={disabled}
              placeholder="∞"
              className={cn(
                'w-full px-3 py-2 pr-8 rounded-xl',
                'bg-slate-50 border border-slate-200',
                'text-sm text-slate-700 placeholder:text-slate-400',
                'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent',
                'transition-all',
                disabled && 'cursor-not-allowed',
              )}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {suffix}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * PaymentMethodControllerList - Container for multiple controllers
 */
export interface PaymentMethodControllerListProps {
  children: React.ReactNode;
  className?: string;
}

export function PaymentMethodControllerList({
  children,
  className,
}: PaymentMethodControllerListProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {children}
    </div>
  );
}
